import { useState } from "react";
import { inr, API } from "@/lib/api";
import { ArrowLeft, Download, Copy, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ResponsibleManagerCard from "@/pages/admin/ResponsibleManagerCard";
import GuestIdSection from "@/pages/admin/GuestIdSection";

export default function BookingDetail({ detail, payForm, setPayForm, rooms = [], onBack, onAddPayment, onVerifyPayment, onResend, onMarkSync, onSetConfStatus, onCopyMessage, onCancel, onDelete, onEdit, onChanged }) {
  const { user } = useAuth();
  const isOwner = user?.role === "SUPER_ADMIN";
  const [showCancel, setShowCancel] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [reason, setReason] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const canCancel = ["DRAFT", "CONFIRMED"].includes(detail.status);
  const openEdit = () => { setEditForm({ full_name: detail.full_name, mobile: detail.mobile, email: detail.email || "", notes: detail.notes || "", check_in_date: detail.check_in_date, check_out_date: detail.check_out_date, room_ids: detail.rooms.map((r) => r.id) }); setShowEdit(true); };
  return (
    <div className="space-y-6" data-testid="booking-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm" style={{ color: "var(--muted)" }} data-testid="booking-detail-back"><ArrowLeft size={16} /> Back to bookings</button>
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-serif text-3xl">{detail.booking_reference}</div>
            <div className="text-sm" style={{ color: "var(--muted)" }}>{detail.full_name} · {detail.mobile} · {detail.email || "no email"}</div>
          </div>
          <span className={detail.status === "CONFIRMED" ? "badge-pine" : "badge-terracotta"}>{detail.status}</span>
        </div>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          <div><div className="text-xs" style={{ color: "var(--muted)" }}>Dates</div>{detail.check_in_date} → {detail.check_out_date} ({detail.number_of_nights} nights)</div>
          <div><div className="text-xs" style={{ color: "var(--muted)" }}>Rooms</div>{detail.rooms.map((r) => r.name).join(", ")}</div>
          <div><div className="text-xs" style={{ color: "var(--muted)" }}>Guests</div>{detail.total_pax}</div>
          <div><div className="text-xs" style={{ color: "var(--muted)" }}>Balance remaining</div><span className="font-serif text-xl" style={{ color: "var(--terracotta)" }} data-testid="bk-detail-balance">{inr(detail.balance_remaining)}</span></div>
        </div>
        {detail.notes && <div className="text-sm mt-3 p-2 rounded" style={{ background: "var(--surface)" }}>Notes: {detail.notes}</div>}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <ResponsibleManagerCard kind="bookings" targetId={detail.id} value={detail.responsible_manager}
          activeStay={detail.status === "CHECKED_IN"} canEdit={isOwner} onSaved={onChanged} />
        <GuestIdSection docs={detail.guest_ids || []} canDelete={isOwner && detail.status === "CHECKED_OUT"} onChanged={onChanged} />
      </div>

      {detail.status !== "DRAFT" && (
        <div className="card" data-testid="voucher-card">
          <h3 className="font-serif text-2xl mb-1">Confirmation Voucher</h3>
          <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>Generated from the live booking & verified payment data. Download it, then send it via your normal WhatsApp or email.</p>
          <div className="flex flex-wrap gap-3 mb-5">
            <a href={`${API}/admin/bookings/${detail.id}/voucher.pdf`} data-testid="bk-download-voucher" className="btn-primary inline-flex items-center gap-2">
              <Download size={16} /> Download Booking Voucher
            </a>
            <button data-testid="bk-copy-message" onClick={onCopyMessage} className="btn-outline inline-flex items-center gap-2">
              <Copy size={16} /> Copy Confirmation Message
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm" style={{ color: "var(--muted)" }}>Confirmation to guest:</span>
            <select data-testid="bk-conf-status" style={{ width: "auto" }}
              value={detail.confirmation_status || "NOT_SENT"}
              onChange={(e) => onSetConfStatus(e.target.value)}>
              <option value="NOT_SENT">Not sent</option>
              <option value="SENT_VIA_WHATSAPP">Sent via WhatsApp</option>
              <option value="SENT_VIA_EMAIL">Sent via Email</option>
              <option value="SENT_VIA_BOTH">Sent via Both</option>
            </select>
            {detail.confirmation_sent_at && (
              <span className="text-xs" style={{ color: "var(--muted)" }}>
                Marked by {detail.confirmation_sent_by} · {new Date(detail.confirmation_sent_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="font-serif text-2xl mb-3">Payment Ledger</h3>
        {detail.payments.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No payments recorded yet.</p>}
        <div className="space-y-2 mb-4">
          {detail.payments.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`bk-pay-${p.id}`}>
              <span className="font-medium">{inr(p.amount)}</span>
              <span className="text-xs" style={{ color: "var(--muted)" }}>{p.method}{p.reference ? ` · ${p.reference}` : ""} · {new Date(p.paid_at).toLocaleDateString()}</span>
              <span className={p.verification_status === "VERIFIED" ? "badge-pine" : "badge-terracotta"}>{p.verification_status}</span>
              {p.verification_status === "VERIFIED" && <span className="text-xs" style={{ color: "var(--muted)" }}>by {p.verified_by}</span>}
              {p.verification_status === "PENDING" && (
                <button data-testid={`bk-pay-verify-${p.id}`} onClick={() => onVerifyPayment(p.id)} className="btn-primary" style={{ padding: "6px 14px", fontSize: 13 }}>Verify</button>
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <input data-testid="bk-addpay-amount" type="number" min="0" placeholder="Amount" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} style={{ width: 130 }} />
          <select data-testid="bk-addpay-method" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} style={{ width: 150 }}>
            <option>UPI</option><option>Bank Transfer</option><option>Cash</option><option>Card</option>
          </select>
          <input data-testid="bk-addpay-ref" placeholder="Reference / UTR" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} style={{ width: 180 }} />
          <button data-testid="bk-addpay-btn" onClick={onAddPayment} className="btn-outline">Record Payment</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-serif text-2xl mb-3">Confirmation</h3>
          <div className="space-y-2 mb-4 text-sm">
            <div className="flex justify-between"><span>WhatsApp</span><span className="badge-terracotta" data-testid="bk-wa-status">{detail.whatsapp_status === "NOT_CONNECTED" ? "NOT CONNECTED" : detail.whatsapp_status}</span></div>
            <div className="flex justify-between"><span>Email</span><span className="badge-terracotta" data-testid="bk-email-status">{detail.email_status === "NOT_CONNECTED" ? "NOT CONNECTED" : detail.email_status}</span></div>
            {detail.last_confirmation_attempt_at && <div className="text-xs" style={{ color: "var(--muted)" }}>Last attempt: {new Date(detail.last_confirmation_attempt_at).toLocaleString()}</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="bk-resend-wa" onClick={() => onResend("whatsapp")} className="btn-outline" style={{ padding: "8px 14px", fontSize: 13 }}>Resend WhatsApp</button>
            <button data-testid="bk-resend-email" onClick={() => onResend("email")} className="btn-outline" style={{ padding: "8px 14px", fontSize: 13 }}>Resend Email</button>
            <button data-testid="bk-resend-both" onClick={() => onResend("both")} className="btn-outline" style={{ padding: "8px 14px", fontSize: 13 }}>Resend Both</button>
          </div>
        </div>
        <div className="card">
          <h3 className="font-serif text-2xl mb-3">External Calendars</h3>
          {["airbnb", "booking_com"].map((pl) => (
            <div key={pl} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span className="text-sm">{pl === "airbnb" ? "Airbnb" : "Booking.com"}</span>
              <span className="badge-terracotta" data-testid={`bk-sync-${pl}`}>{detail[`sync_${pl}_status`]?.replaceAll("_", " ")}</span>
              {detail[`sync_${pl}_status`] === "ACTION_REQUIRED" && (
                <button data-testid={`bk-sync-mark-${pl}`} onClick={() => onMarkSync(pl)} className="btn-pine" style={{ padding: "6px 14px", fontSize: 13 }}>
                  Mark {pl === "airbnb" ? "Airbnb" : "Booking.com"} Blocked ✓
                </button>
              )}
            </div>
          ))}
          <p className="text-xs mt-3" style={{ color: "var(--muted)" }}>No automatic OTA sync is connected. Block these dates manually on each platform, then mark them here.</p>
        </div>
      </div>

      {isOwner && (
        <div className="card" style={{ border: "1px solid rgba(178,58,42,.4)" }} data-testid="booking-danger-zone">
          <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><AlertTriangle size={20} style={{ color: "var(--terracotta)" }} /> Owner Actions</h3>
          <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>Cancel keeps the full history and releases the rooms immediately. Delete is only for safe dummy/test bookings.</p>
          <div className="flex flex-wrap gap-3">
            <button data-testid="bk-edit-btn" onClick={openEdit} className="btn-outline">Edit Booking</button>
            {canCancel && <button data-testid="bk-cancel-btn" onClick={() => setShowCancel(true)} className="btn-outline">Cancel Booking</button>}
            <button data-testid="bk-delete-btn" onClick={() => setShowDelete(true)} className="btn-outline" style={{ color: "var(--terracotta)", borderColor: "rgba(178,58,42,.5)" }}>Delete Test Booking</button>
          </div>
        </div>
      )}

      {showEdit && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.45)" }} data-testid="edit-dialog">
          <div className="card max-w-lg w-full" style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <h3 className="font-serif text-2xl mb-4">Edit Booking {detail.booking_reference}</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <input data-testid="edit-name" placeholder="Guest name" value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} />
              <input data-testid="edit-mobile" placeholder="Mobile" value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} />
              <input data-testid="edit-email" type="email" placeholder="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="md:col-span-2" />
            </div>
            {canCancel && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div><label className="text-xs" style={{ color: "var(--muted)" }}>Check-in</label><input data-testid="edit-checkin" type="date" value={editForm.check_in_date} onChange={(e) => setEditForm({ ...editForm, check_in_date: e.target.value })} /></div>
                  <div><label className="text-xs" style={{ color: "var(--muted)" }}>Check-out</label><input data-testid="edit-checkout" type="date" value={editForm.check_out_date} onChange={(e) => setEditForm({ ...editForm, check_out_date: e.target.value })} /></div>
                </div>
                <div className="mb-3">
                  <label className="text-xs block mb-1" style={{ color: "var(--muted)" }}>Rooms</label>
                  <div className="flex flex-wrap gap-2">
                    {rooms.map((r) => (
                      <button key={r.id} type="button" data-testid={`edit-room-${r.id}`}
                        onClick={() => setEditForm({ ...editForm, room_ids: editForm.room_ids.includes(r.id) ? editForm.room_ids.filter((x) => x !== r.id) : [...editForm.room_ids, r.id] })}
                        className="text-sm px-3 py-1 rounded-full"
                        style={{ background: editForm.room_ids.includes(r.id) ? "var(--pine)" : "var(--card)", color: editForm.room_ids.includes(r.id) ? "#fff" : "var(--fg)", border: "1px solid var(--border)" }}>
                        {r.name}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <label className="text-xs block mb-1" style={{ color: "var(--muted)" }}>Notes</label>
            <textarea data-testid="edit-notes" rows="2" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mb-3" />
            <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>Amounts and payments are never editable here, to protect financial history.</p>
            <div className="flex gap-3">
              <button data-testid="edit-save" onClick={() => { onEdit(editForm); setShowEdit(false); }} className="btn-primary flex-1">Save Changes</button>
              <button data-testid="edit-abort" onClick={() => setShowEdit(false)} className="btn-outline flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.45)" }} data-testid="cancel-dialog">
          <div className="card max-w-md w-full">
            <h3 className="font-serif text-2xl mb-4">Cancel Booking</h3>
            <div className="space-y-2 text-sm mb-4">
              <DRow label="Guest" value={detail.full_name} />
              <DRow label="Booking ID" value={detail.booking_reference} />
              <DRow label="Dates" value={`${detail.check_in_date} → ${detail.check_out_date}`} />
              <DRow label="Room(s)" value={detail.rooms.map((r) => r.name).join(", ")} />
              <DRow label="Total amount" value={inr(detail.booking_amount)} />
              <DRow label="Verified payments" value={inr(detail.verified_paid)} accent={detail.verified_paid > 0} />
              <DRow label="Outstanding" value={inr(detail.balance_remaining)} accent />
            </div>
            {detail.verified_paid > 0 && (
              <p className="text-xs mb-3 p-2 rounded" style={{ background: "var(--surface)", color: "var(--terracotta)" }}>
                This booking has verified payments of {inr(detail.verified_paid)}. Cancellation does NOT mark them refunded — handle any refund separately.
              </p>
            )}
            <label className="text-xs block mb-1" style={{ color: "var(--muted)" }}>Cancellation reason (optional)</label>
            <textarea data-testid="cancel-reason" rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className="mb-4" />
            <div className="flex gap-3">
              <button data-testid="cancel-confirm" onClick={() => { onCancel(reason); setShowCancel(false); setReason(""); }} className="btn-primary flex-1">Confirm Cancellation</button>
              <button data-testid="cancel-abort" onClick={() => setShowCancel(false)} className="btn-outline flex-1">Keep Booking</button>
            </div>
          </div>
        </div>
      )}

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="delete-dialog">
          <div className="card max-w-md w-full">
            <h3 className="font-serif text-2xl mb-2" style={{ color: "var(--terracotta)" }}>Permanently delete this test booking?</h3>
            <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>
              Booking {detail.booking_reference} ({detail.full_name}) will be permanently removed. If it has any real check-in, verified payment, order or bill history, the system will refuse and ask you to Cancel instead.
            </p>
            <div className="flex gap-3">
              <button data-testid="delete-confirm" onClick={() => { onDelete(); setShowDelete(false); }} className="btn-primary flex-1" style={{ background: "var(--terracotta)" }}>Yes, Delete Permanently</button>
              <button data-testid="delete-abort" onClick={() => setShowDelete(false)} className="btn-outline flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DRow({ label, value, accent }) {
  return (
    <div className="flex justify-between">
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span className="font-medium" style={{ color: accent ? "var(--terracotta)" : "var(--fg)" }}>{value}</span>
    </div>
  );
}
